// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Provider-agnostic helper that maps checkout-return URL params from BOTH
 * Stripe (`?status=success|canceled`) AND Lemon Squeezy (`?order_id=…` /
 * `?checkout_id=…`) into a single, normalised `status` field that the billing
 * page renders. Kept in `lib/` rather than alongside the route file so the
 * route module stays "components-only" (Fast Refresh requirement).
 */

/**
 * Normalise the checkout-return params across providers.
 *
 * Stripe is configured in the billing router to redirect with
 * `?status=success|canceled`. Lemon Squeezy uses the same `successUrl` we hand
 * it but may append its own params (e.g. `order_id`). To be resilient — and to
 * avoid silently dropping a successful payment toast if a provider changes its
 * redirect contract — we accept either signal and project both into a single
 * `status` field that the page renders.
 */
export function normaliseBillingSearch(search: Record<string, unknown>): {
	status?: "success" | "canceled";
	purchase?: "addon";
	addOnId?: string;
	organization_id?: string;
	order_id?: string;
	checkout_id?: string;
} {
	const rawStatus = search.status;
	const orderId = search.order_id ?? search.orderId;
	const checkoutId = search.checkout_id ?? search.checkoutId;
	const normalisedStatus: "success" | "canceled" | undefined =
		rawStatus === "success" || rawStatus === "addon_success"
			? "success"
			: rawStatus === "canceled" || rawStatus === "addon_canceled"
				? "canceled"
				: orderId || checkoutId
					? "success"
					: undefined;
	const addOnId = search.addOnId ?? search.add_on_id;
	const result: ReturnType<typeof normaliseBillingSearch> = {
		status: normalisedStatus,
		order_id: typeof orderId === "string" ? orderId : undefined,
		checkout_id: typeof checkoutId === "string" ? checkoutId : undefined,
	};
	if (
		search.purchase === "addon" ||
		rawStatus === "addon_success" ||
		rawStatus === "addon_canceled"
	) {
		result.purchase = "addon";
	}
	if (typeof addOnId === "string") result.addOnId = addOnId;
	if (typeof search.organization_id === "string") {
		result.organization_id = search.organization_id;
	}
	return result;
}
